/**
 * The two picture slots' reading and saving. A picture is saved on the draft
 * when it is chosen and read into its six lines at once, so the design run
 * finds the lines and reads nothing again. The answer for a picture is kept
 * only while that picture is still in the slot: the host may have replaced it
 * while the read was on its way (specs/formal/inspiration-read).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { PageInspirations, PageSlot, StructureBrief } from "../../../shared/types";
import { api } from "../../lib/api";
import { SLOTS } from "../inspirations";

export type ReadState = { key: string; state: "reading" | "failed" };
export type Reads = Partial<Record<PageSlot, ReadState>>;

const EDIT_SAVE_MS = 700;

export function useShotReads(webinarId: string, value: PageInspirations, setValue: (next: PageInspirations) => void) {
  const live = useRef(value);
  live.current = value;
  const [reads, setReads] = useState<Reads>({});
  const tried = useRef<Partial<Record<PageSlot, string>>>({});
  // Why a chain: two saves that cross on the way would leave the older one last.
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const timer = useRef<number | null>(null);

  const persist = useCallback(() => {
    const run = chain.current.then(() => api.pages.inspirations(webinarId, { inspirations: live.current }));
    chain.current = run.catch(() => undefined);
    return run;
  }, [webinarId]);

  const put = useCallback(
    (next: PageInspirations) => {
      live.current = next;
      setValue(next);
    },
    [setValue],
  );

  const change = useCallback(
    (next: PageInspirations) => {
      put(next);
      void persist().catch(() => undefined);
    },
    [persist, put],
  );

  const editLine = useCallback(
    (slot: PageSlot, line: keyof StructureBrief, text: string) => {
      const one = live.current[slot];
      if (!one?.brief) return;
      put({ ...live.current, [slot]: { ...one, brief: { ...one.brief, [line]: text } } });
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        void persist().catch(() => undefined);
      }, EDIT_SAVE_MS);
    },
    [persist, put],
  );

  const read = useCallback(
    async (slot: PageSlot, key: string) => {
      setReads((r) => ({ ...r, [slot]: { key, state: "reading" } }));
      const done = (state: ReadState | undefined) => setReads((r) => ({ ...r, [slot]: state }));
      try {
        await persist();
        const got = (await api.pages.readPicture(webinarId, slot)).inspirations[slot];
        const now = live.current[slot];
        // The slot holds another picture now: this answer is not for it.
        if (now?.key !== key) return;
        if (got?.key === key && got.brief) {
          if (!now.brief) put({ ...live.current, [slot]: { ...now, brief: got.brief } });
          done(undefined);
        } else {
          done({ key, state: "failed" });
        }
      } catch {
        if (live.current[slot]?.key === key) done({ key, state: "failed" });
      }
    },
    [persist, put, webinarId],
  );

  useEffect(() => {
    for (const slot of SLOTS) {
      const one = value[slot];
      if (!one) {
        delete tried.current[slot];
        continue;
      }
      if (one.brief || tried.current[slot] === one.key) continue;
      tried.current[slot] = one.key;
      void read(slot, one.key);
    }
  }, [value, read]);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const reading = SLOTS.some((s) => reads[s]?.state === "reading" && reads[s]?.key === value[s]?.key);
  return { reads, reading, change, editLine };
}
